import { AfterViewInit, ChangeDetectionStrategy, Component, inject, OnInit, viewChild } from '@angular/core';
import { AppStorage } from '@app/common/services/data-api/app-storage.service';
import { ButtonLoadingDirective } from '@app/shared/directives/button-loading/button-loading.directive';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { AgGridAngular } from 'ag-grid-angular';
import { ForecastBrokerService } from '../services/forecast-broker.service';
import { ForecastService } from '../services/forecast.service';
import { detailForecastRequest, ForecastScenario, forecastScenariosGridEvents, forecastScenariosGridOptions } from './forecastScenariosGridOptions';

@Component({
  selector: 'app-forecast-scenarios',
  templateUrl: './forecast-scenarios.component.html',
  styleUrls: ['./forecast-scenarios.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AgGridAngular,
    ButtonDirective,
    ButtonLoadingDirective,
  ],
})
export class ForecastScenariosComponent implements OnInit, AfterViewInit {
  public service = inject(ForecastService);
  private broker = inject(ForecastBrokerService);
  private store = inject(AppStorage);

  private readonly agGrid = viewChild(AgGridAngular);

  public icons = kendoIcons;
  public forecastScenariosGridOptions = forecastScenariosGridOptions;
  public forecastScenariosGridEvents = forecastScenariosGridEvents;
  public isForecastRunning: boolean = false;
  public forecastMessage: string = 'Forecast in Progress for Scenario';
  public editedScenarios: Map<number, ForecastScenario> = new Map<any, ForecastScenario>();
  public loading: boolean = false;

  ngOnInit() {
    detailForecastRequest.subscribe((params) => {
      if (params?.column) {
        this.onCellValueChanged(params);
      } else if (params?.data) {
        this.getHistoricalForecastData(params);
      }
    });

    this.loadData();

    this.forecastScenariosGridEvents.selectionChanged.subscribe(scenario => {
      this.broker.scenarioSelectionChanged.next(scenario);
    });

    this.forecastScenariosGridEvents.cellDoubleClicked.subscribe(scenario => {
      this.broker.cellDoubleClicked.next(scenario);
    });

    //now that we're using Hangfire, we can queue up multiple forecasts... 
    //if we need to re-enable the banner that shows a message if a forecast is running, we can add that logic here...
    //for example:

    // this.service.isCapitalForecastRunning().then((res) => {
    //   if (res.IsRunning) {
    //     this.isForecastRunning = res.IsRunning;
    //     this.forecastMessage = `${this.forecastMessage} ${res.ScenarioId}`;
    //   }
    // });
  }

  ngAfterViewInit() {
    this.store.knownEvents.assetTeamSelected.subscribe(selected => {
      if (!selected) return;
      this.loadData();
    });
  }

  private loadData() {
    this.loading = true;
    this.service.getAvailableForecastScenarios().subscribe(x => {
      this.agGrid()?.api?.setGridOption('rowData', x);
      this.loading = false;
    });
  }

  getHistoricalForecastData(params: any) {

    this.service.getHistoricalForecastScenarios(params.data.ScenarioId).subscribe(historicalData => {
      if (historicalData.length === 0) {
        params.successCallback([{ CapitalForecastLogId: 'No historical data available.' }]);
      } else {
        params.successCallback(historicalData);
      }
    },
      () => {
        params.successCallback([{ CapitalForecastLogId: 'Error fetching data' }]);
      }
    );
  }

  cancelEdit() {
    this.editedScenarios = new Map<any, ForecastScenario>();
    this.loadData();
  }

  onCellValueChanged(e) {
    this.trackChanges(e);
  }

  trackChanges(e) {
    let scenario: ForecastScenario = {
      CapitalForecastLogId: e.data.CapitalForecastLogId,
      ScenarioId: e.data.ScenarioId,
      Comments: e.data.Comments
    };

    this.editedScenarios.set(e.node.id, scenario);
  }

  saveChanges() {
    if (this.loading) return;
    this.loading = true;
    let scenarios = this.editedScenarios.values();
    this.service.updateForecastDetails(Array.from(scenarios)).subscribe(x => {
      this.editedScenarios = new Map<any, ForecastScenario>();
      this.loading = false;
      this.loadData();
    });
  }
}